class RsUgcVideoScroller extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {number} */
  #activeIndex = 0;
  /** @type {boolean} */
  #userPaused = false;
  /** @type {number} */
  #scrollTimer = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-ugc-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-ugc-slide]'));
    if (!this.#track || this.#slides.length === 0) return;

    this.#track.addEventListener('scroll', () => this.#onScroll(), { passive: true });
    this.addEventListener('click', (event) => this.#onClick(event));

    this.#slides.forEach((slide, index) => {
      const video = this.#video(slide);
      if (!video) return;
      video.muted = true;
      video.playsInline = true;
      video.addEventListener('ended', () => this.#onEnded(index));
    });

    this.#setActive(0, { play: this.#autoplayEnabled });

    if (this.#track instanceof HTMLElement) {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting) {
            this.#playActive();
          } else {
            this.#pauseAll();
          }
        },
        { threshold: 0.35 }
      );
      observer.observe(this);
    }
  }

  get #autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #autoScrollEnabled() {
    return this.dataset.autoScroll === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLVideoElement | null}
   */
  #video(slide) {
    const video = slide.querySelector('video');
    return video instanceof HTMLVideoElement ? video : null;
  }

  #onScroll() {
    window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => {
      const next = this.#closestIndex();
      if (next !== this.#activeIndex) {
        this.#userPaused = false;
        this.#setActive(next, { play: this.#autoplayEnabled });
      }
    }, 80);
  }

  #closestIndex() {
    if (!this.#track) return 0;
    const origin = this.#track.getBoundingClientRect().left;
    let best = 0;
    let bestDistance = Infinity;

    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - origin);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick(event) {
    const slide = event.target instanceof Element ? event.target.closest('[data-ugc-slide]') : null;
    if (!(slide instanceof HTMLElement)) return;

    const index = this.#slides.indexOf(slide);
    if (index < 0) return;

    if (index !== this.#activeIndex) {
      this.#userPaused = false;
      this.#scrollTo(index);
      this.#setActive(index, { play: true, unmute: true });
      return;
    }

    const video = this.#video(slide);
    if (!video) return;

    if (video.paused) {
      this.#userPaused = false;
      video.muted = false;
      this.#play(video);
      slide.setAttribute('data-playing', '');
    } else if (video.muted) {
      video.muted = false;
    } else {
      video.pause();
      this.#userPaused = true;
      slide.removeAttribute('data-playing');
    }
  }

  /**
   * @param {number} index
   */
  #onEnded(index) {
    if (index !== this.#activeIndex || !this.#autoScrollEnabled) return;
    const next = (index + 1) % this.#slides.length;
    this.#userPaused = false;
    this.#scrollTo(next);
    this.#setActive(next, { play: this.#autoplayEnabled });
  }

  /**
   * @param {number} index
   */
  #scrollTo(index) {
    const slide = this.#slides[index];
    if (!slide || !this.#track) return;
    this.#track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  /**
   * @param {number} index
   * @param {{ play?: boolean, unmute?: boolean }} [options]
   */
  #setActive(index, options = {}) {
    this.#activeIndex = index;

    this.#slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.toggleAttribute('data-active', isActive);

      const video = this.#video(slide);
      if (!video) return;

      if (isActive && options.play && !this.#userPaused) {
        if (options.unmute) video.muted = false;
        this.#play(video);
        slide.setAttribute('data-playing', '');
      } else if (!isActive) {
        video.pause();
        video.currentTime = 0;
        video.muted = true;
        slide.removeAttribute('data-playing');
      }
    });
  }

  #playActive() {
    if (!this.#autoplayEnabled || this.#userPaused) return;
    const slide = this.#slides[this.#activeIndex];
    const video = slide ? this.#video(slide) : null;
    if (video) this.#play(video);
  }

  #pauseAll() {
    this.#slides.forEach((slide) => {
      const video = this.#video(slide);
      if (video) video.pause();
    });
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #play(video) {
    const playAttempt = video.play();
    if (playAttempt instanceof Promise) {
      playAttempt.catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  }
}

if (!customElements.get('rs-ugc-video-scroller')) {
  customElements.define('rs-ugc-video-scroller', RsUgcVideoScroller);
}
